/**
 * Rows for the Won tab (BAR-2805): one per patient, on their newest Win in the
 * range. A Win its REVERSAL undoes is dropped.
 */

import { getReferenceString } from '@medplum/core';
import { Appointment, Patient, Practitioner } from '@medplum/fhirtypes';
import { getCareTeamMembersInPool } from '@bariendo/lib/care-team-roles';
import { Track } from '@bariendo/lib/constants';
import { readEligibilityIntakeSelections, readEligibilityPayment } from '@bariendo/lib/eligibility-intake';
import { getTrackOrSelfPay } from '@bariendo/lib/episode-of-care';
import { readInterestedService, readMarketLocation } from '@bariendo/lib/intake-selection';
import { parsePatientAccessCallAnswers } from '@bariendo/lib/pac-call-notes';
import { readEmployer } from '@bariendo/lib/pac-inquiry-list';
import { PacQueueHydration } from '../hooks/usePacQueueHydration';
import { WonEvent } from '../hooks/useWonEvents';
import { StickyNoteCell, toStickyNoteCell } from './listRowShared';

export interface WonLeadRow {
  id: string;
  patient: Patient;
  wonAt: string;
  /** The ledger's track as of the Win: `d2c`, `employer` or `insurance` (BAR-3086). */
  wonTrack: string;
  /** The booked index procedure; absent when none is. */
  procedureAppointment?: Appointment;
  patientNavigatorPractitioners: Practitioner[];
  location?: string;
  /** The service the lead asked about, for the Patient cell. */
  procedure?: string;
  track?: Track;
  employer?: string;
  stickyNote?: StickyNoteCell;
}

export type WonProcedureFilter = 'all' | 'scheduled' | 'not-scheduled';

const NOT_BOOKED_STATUSES: readonly Appointment['status'][] = ['cancelled', 'noshow', 'entered-in-error'];

/** Each patient's newest unreversed Win, newest first. Events with no Medplum Patient can't be shown. */
export function latestWins(events: WonEvent[]): WonEvent[] {
  const reversed = new Set(events.filter((event) => event.kind === 'REVERSAL').map((event) => event.reversesId));
  const byPatient = new Map<string, WonEvent>();

  for (const event of events) {
    if (event.kind !== 'WON' || !event.medplumPatientId || reversed.has(event.id)) {
      continue;
    }
    const held = byPatient.get(event.medplumPatientId);
    if (!held || Date.parse(event.occurredAt) > Date.parse(held.occurredAt)) {
      byPatient.set(event.medplumPatientId, event);
    }
  }

  return [...byPatient.values()].sort((a, b) => Date.parse(b.occurredAt) - Date.parse(a.occurredAt));
}

export function buildWonRows(
  wins: WonEvent[],
  patientById: Map<string, Patient>,
  hydration: PacQueueHydration
): WonLeadRow[] {
  return wins.flatMap((win) => {
    const patient = win.medplumPatientId ? patientById.get(win.medplumPatientId) : undefined;
    const patientRef = patient ? getReferenceString(patient) : undefined;
    if (!patient?.id || !patientRef) {
      return [];
    }

    const callNote = hydration.callNotesByPatient.get(patientRef)?.[0];
    const callAnswers = callNote ? parsePatientAccessCallAnswers(callNote) : undefined;
    const eligibility = hydration.eligibilityByPatient.get(patientRef);
    const eligibilitySelections = readEligibilityIntakeSelections(eligibility);
    const episode = hydration.episodeByPatient.get(patientRef);
    const procedure = hydration.procedureByPatient.get(patientRef);
    const navigatorRefs = getCareTeamMembersInPool(
      hydration.careTeamByPatient.get(patientRef),
      hydration.patientNavigatorPool
    );

    return [
      {
        id: patient.id,
        patient,
        wonAt: win.occurredAt,
        wonTrack: win.track,
        procedureAppointment: procedure && !NOT_BOOKED_STATUSES.includes(procedure.status) ? procedure : undefined,
        patientNavigatorPractitioners: navigatorRefs
          .map((reference) => hydration.practitionerByRef.get(reference))
          .filter((practitioner): practitioner is Practitioner => practitioner !== undefined),
        location: readMarketLocation(patient, callAnswers, eligibilitySelections.market),
        procedure: readInterestedService(patient, callAnswers, eligibilitySelections.procedure),
        track: getTrackOrSelfPay(episode),
        employer: readEmployer(patient) ?? readEligibilityPayment(eligibility).employer,
        stickyNote: toStickyNoteCell(hydration.stickyNoteByPatient.get(patientRef), hydration.practitionerByRef),
      },
    ];
  });
}

export function matchesProcedureFilter(row: WonLeadRow, filter: WonProcedureFilter): boolean {
  return filter === 'all' || (filter === 'scheduled') === Boolean(row.procedureAppointment);
}
